// @ts-check
import classNames from 'classnames';
import React from 'react';

import './ActionColumns.scss';

/**
 * Get style name based on number of children.
 * @param {any[]} children
 * @returns Class to add.
 */
function getColByChildren(children) {
	if (children.length % 6 === 0 || children.length % 5 === 0) {
		return 'col-md-4';
	} else if (children.length % 4 === 0) {
		return 'col-md-3';
	} else if (children.length % 3 === 0) {
		return 'col-md-4';
	} else if (children.length % 2 === 0) {
		return 'col-md-6';
	} else if (children.length === 1) {
		return 'col-md-12';
	} else {
		return 'col-md-3';
	}
}

/**
 * @typedef ActionColumnsProps
 * @property {boolean} [alignCenter=false] An optional value indicating whether the text should be centered.
 * @property {boolean} [isContainer=true] An optional value indicating whether the container action-col classes should be removed in Action columns.
 * @property {'inner' | 'outer'} [borders] An optional type of border for the Actions columns.
 * @property {boolean} [grow=false] An optional value indicating whether the columns should grow beyond standard.
 */
/** @type {import('~/types/React').ReactFC<ActionColumnsProps>} */
const ActionColumns = ({ alignCenter = false, isContainer = true, borders, children, grow = false, ...rest }) => {
	/** @type {import('react').ReactNode[]} */
	const toMap = Array.isArray(children) ? children : [children];

	/**
	 *
	 * @param {import('react').ReactNode[]} row The array of content for the row.
	 * @param {import('react').ReactElement} ele The element to copy attributes from
	 * @returns {string} The class name to use for this row.
	 */
	const getClassName = (row, ele) => {
		return classNames([
			getColByChildren(row),
			{
				'text-align': alignCenter,
				'do-grow': grow,
				'with-border': borders === 'outer',
				'inner-borders': borders === 'inner',
				'action-col container': isContainer,
			},
			ele.props?.className,
		]);
	};

	/** @type {import('react').ReactNode[][]} */
	const individualRows = [[]];
	for (const child of toMap) {
		const withRoom = individualRows.find((r) => r.length < toMap.length);
		if (!withRoom) {
			individualRows.push([child]);
		} else {
			withRoom.push(child);
		}
	}

	return (
		<React.Fragment>
			{individualRows.map((row, i) => (
				<div {...rest} className={classNames('container action-columns', rest.className, `row-${i}`)} key={i}>
					<div className="row action-row">
						{row.map((ele, i) =>
							/* @ts-ignore - cloneElement accepts these elements too. */
							React.cloneElement(ele, { className: getClassName(row, ele), key: i })
						)}
					</div>
				</div>
			))}
		</React.Fragment>
	);
};

export default ActionColumns;
